'use client';

import React, { useEffect, useRef, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { Body } from '@depop/web-thrift-ui/Typography';
import clsx from 'clsx';

import { PopularTiles } from './Desktop/PopularTiles';
import { NavigationCategoryList } from './NavigationCategoryList';
import styles from './styles.module.css';

import {
  type CategoryList,
  type ImageLinkList,
  MegaNavContentType,
} from '@/modules/meganav/types';
import { useNavConfig } from '@/modules/meganav/useNavConfig';
import { ROUTE_CHANGE_START_EVENT } from '@/modules/routing/constants';

type AriaExpandedState = Record<string, boolean>;

let hoverTimer: number;

export function MegaNav() {
  const { config } = useNavConfig();
  const menubarRef = useRef<HTMLUListElement>(null);

  const ariaExpandedInitialState = config.reduce<AriaExpandedState>(
    (prev, curr) => {
      if (curr && curr.label) {
        return { ...prev, [curr?.label]: false };
      }
      return prev;
    },
    {}
  );

  const [ariaExpandedLookup, setAriaExpandedLookup] =
    useState<AriaExpandedState>(ariaExpandedInitialState);

  function renderHoverContent(list: CategoryList | ImageLinkList | null) {
    switch (list?.__typename) {
      case MegaNavContentType.CategoryList:
        return (
          <NavigationCategoryList
            config={list}
            className={clsx({
              [styles.navCategoryListWide]:
                list.itemsCollection.items.length > 8,
              [styles.navCategoryListNarrow]:
                list.itemsCollection.items.length <= 8,
            })}
          />
        );
      case MegaNavContentType.ImageLinkList:
        return <PopularTiles config={list} />;
      default:
        return null;
    }
  }

  function addAriaExpanded(label: string) {
    setAriaExpandedLookup((prev) => ({
      ...prev,
      [label]: true,
    }));
  }

  function removeAriaExpanded(label: string) {
    setAriaExpandedLookup((prev) => ({
      ...prev,
      [label]: false,
    }));
  }

  function closeAll() {
    if (hoverTimer) {
      window.clearTimeout(hoverTimer);
    }
    setAriaExpandedLookup((prev) => {
      const reset: AriaExpandedState = {};
      for (const key of Object.keys(prev)) {
        reset[key] = false;
      }
      return reset;
    });
  }

  function handleMouseOver(itemLabel: string) {
    if (hoverTimer) {
      window.clearTimeout(hoverTimer);
    }

    // eslint-disable-next-line react-hooks/globals
    hoverTimer = window.setTimeout(() => addAriaExpanded(itemLabel), 200);
  }

  function handleMouseLeave(itemLabel: string) {
    if (hoverTimer) {
      window.clearTimeout(hoverTimer);
    }

    removeAriaExpanded(itemLabel);
  }

  function handleMenubarKeyDown(e: React.KeyboardEvent<HTMLUListElement>) {
    const buttons =
      menubarRef.current?.querySelectorAll<HTMLButtonElement>(
        '[role="menuitem"]'
      );
    if (!buttons?.length) {
      return;
    }

    const currentIndex = Array.from(buttons).findIndex(
      (btn) => btn === document.activeElement
    );
    if (currentIndex === -1) {
      return;
    }

    switch (e.key) {
      case 'ArrowRight': {
        e.preventDefault();
        const nextIndex = (currentIndex + 1) % buttons.length;
        closeAll();
        buttons[nextIndex].focus();
        break;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        const prevIndex = (currentIndex - 1 + buttons.length) % buttons.length;
        closeAll();
        buttons[prevIndex].focus();
        break;
      }
      case 'Escape': {
        e.preventDefault();
        closeAll();
        buttons[currentIndex].focus();
        break;
      }
    }
  }

  useEffect(() => {
    function handleRouteChange() {
      closeAll();
    }

    document.addEventListener(ROUTE_CHANGE_START_EVENT, handleRouteChange);

    return () => {
      document.removeEventListener(ROUTE_CHANGE_START_EVENT, handleRouteChange);
    };
  }, []);

  try {
    /* eslint-disable react-hooks/error-boundaries */
    return (
      <nav className={styles.container} aria-label="Main categories">
        <ul
          className={styles.menuList}
          role="menubar"
          ref={menubarRef}
          onKeyDown={handleMenubarKeyDown}
        >
          {config.map((item) => (
            <React.Fragment key={item.label}>
              <li
                className={styles.menuListItem}
                role="none"
                style={
                  item.colourOverride
                    ? ({
                        '--textColor': item.colourOverride,
                        '--hoverBg': item.colourOverride,
                      } as React.CSSProperties)
                    : {}
                }
                onMouseOver={() => handleMouseOver(item.label)}
                onMouseLeave={() => handleMouseLeave(item.label)}
                onFocus={() => handleMouseOver(item.label)}
                onBlur={(e) => {
                  const nextTarget = e.relatedTarget;
                  if (
                    !nextTarget ||
                    !(nextTarget instanceof Node) ||
                    !e.currentTarget.contains(nextTarget)
                  ) {
                    handleMouseLeave(item.label);
                  }
                }}
              >
                <button
                  type="button"
                  role="menuitem"
                  aria-haspopup="true"
                  style={
                    item.colourOverride
                      ? ({
                          '--textColor': item.colourOverride,
                          '--hoverBg': item.colourOverride,
                        } as React.CSSProperties)
                      : {}
                  }
                  className={styles.menuListItemButton}
                  onClick={() =>
                    ariaExpandedLookup[item.label]
                      ? removeAriaExpanded(item.label)
                      : addAriaExpanded(item.label)
                  }
                  aria-expanded={ariaExpandedLookup[item.label]}
                >
                  <Body variant="sm-bold" as="span">
                    {item.label}
                  </Body>
                </button>
                <div
                  className={clsx(styles.hoverContainer, {
                    [styles.hoverContainerActive]:
                      ariaExpandedLookup[item.label],
                  })}
                >
                  {item.contentCollection.items.map((list, index) => (
                    <React.Fragment key={index}>
                      {renderHoverContent(list)}
                    </React.Fragment>
                  ))}
                </div>
              </li>
            </React.Fragment>
          ))}
        </ul>
      </nav>
    );
  } catch (error) {
    // log error to Datadog
    const renderError = new Error((error as Error).message);
    renderError.name = 'NavigationV2_RENDER_ERROR';
    renderError.cause = error;
    datadogRum.addError(renderError);
    return null;
  }
}
